import { Dialog } from '@angular/cdk/dialog';
import {
  Component,
  effect,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs';
import { CsvColumn } from 'src/app/models/csv-export.model';
import { DeviceDto } from 'src/app/models/device.model';
import { UserRole } from 'src/app/models/user.model';
import { CsvExportService } from 'src/app/services/csv-export.service';
import { DeviceStore } from 'src/app/services/device.store';
import { selectProfileData } from 'src/app/stores/profile/profile.selector';
import { buildTimestamp } from 'src/utils/general-utils';
import { EditDeviceModalComponent } from '../modals/edit-device-modal/edit-device-modal.component';
import { matchesSearch } from 'src/app/utils/text-search';
import { InformationModalComponent } from '../modals/information-modal/information-modal.component';
import {
  ActionButtonColumnDefinition,
  ActionMenuColumnDefinition,
  ColumnDefinition,
  DataColumnDefinition,
  PaginatorConfig,
  TableAction,
} from '../qs-table/column-definition.interface';

interface FilterOption {
  label: string;
  value: string;
}

@Component({
  selector: 'app-device-table',
  standalone: false,
  templateUrl: './device-table.component.html',
  styleUrl: './device-table.component.scss',
})
export class DeviceTableComponent implements OnInit, OnChanges {
  private readonly deviceStore = inject(DeviceStore);
  private readonly translateService = inject(TranslateService);
  private readonly csvExportService = inject(CsvExportService);
  private readonly router = inject(Router);
  private readonly dialog = inject(Dialog);
  private readonly store = inject(Store);
  private readonly profile = toSignal(this.store.select(selectProfileData));
  private readonly deletePermissionTooltip = 'only admin can delete';

  paginatorConfig: PaginatorConfig = {
    pageSize: 15,
    pageSizeOptions: [5, 10, 15, 25],
  };

  readonly isSearchEnabled = false;
  readonly isPaginatorEnabled = true;
  readonly allFilterValue = '__ALL__';
  readonly unassignedCompanyFilterValue = '__UNASSIGNED__';

  @Input() useAllDevices = false;
  @Input() showDeleteAction = true;

  searchUnitNo = '';
  selectedDeviceType = this.allFilterValue;
  selectedModel = this.allFilterValue;
  selectedCompany = this.allFilterValue;
  filteredDevices: DeviceDto[] = [];
  deviceTypeFilterOptions: FilterOption[] = [];
  modelFilterOptions: FilterOption[] = [];
  companyFilterOptions: FilterOption[] = [];
  columns: ColumnDefinition<DeviceDto>[] = [];

  readonly nameColumn: ColumnDefinition<DeviceDto> = {
    type: 'data',
    field: 'nameAlias',
    testId: 'name',
    header: this.translateService.instant('resources.table.name'),
    format: (row) => this.getName(row),
    tooltip: (row) => this.getNameTooltip(row),
    sortFn: (a, b, multiplier) =>
      this.getName(a).localeCompare(this.getName(b)) * multiplier,
  } as DataColumnDefinition<DeviceDto>;

  readonly companyColumn: ColumnDefinition<DeviceDto> = {
    type: 'data',
    field: 'company',
    header: this.translateService.instant('resources.filter.company'),
    format: (row) => this.getCompanyLabel(row),
    sortFn: (a, b, multiplier) =>
      this.getCompanyLabel(a).localeCompare(this.getCompanyLabel(b)) *
      multiplier,
  } as DataColumnDefinition<DeviceDto>;

  readonly deviceTypeColumn: ColumnDefinition<DeviceDto> = {
    type: 'data',
    field: 'details.type',
    header: this.translateService.instant('resources.table.device_type'),
    format: (row) => this.getDeviceType(row),
    sortFn: (a, b, multiplier) =>
      this.getDeviceType(a).localeCompare(this.getDeviceType(b)) * multiplier,
  } as DataColumnDefinition<DeviceDto>;

  readonly modelColumn: ColumnDefinition<DeviceDto> = {
    type: 'data',
    field: 'model',
    header: this.translateService.instant('resources.table.model'),
    format: (row) => this.getAircraftModelName(row),
    sortFn: (a, b, multiplier) =>
      this.getAircraftModelName(a).localeCompare(this.getAircraftModelName(b)) *
      multiplier,
  } as DataColumnDefinition<DeviceDto>;

  readonly unitIdColumn: ColumnDefinition<DeviceDto> = {
    type: 'data',
    field: 'unitId',
    header: this.translateService.instant('resources.table.unit_id'),
    format: (row) => this.getUnitId(row),
    sortFn: (a, b, multiplier) =>
      this.getUnitId(a).localeCompare(this.getUnitId(b)) * multiplier,
  } as DataColumnDefinition<DeviceDto>;

  readonly baseColumns: ColumnDefinition<DeviceDto>[] = [
    this.nameColumn,
    this.deviceTypeColumn,
    this.modelColumn,
    this.unitIdColumn,
  ];

  readonly allResourcesColumns: ColumnDefinition<DeviceDto>[] = [
    this.nameColumn,
    this.companyColumn,
    this.deviceTypeColumn,
    this.modelColumn,
    this.unitIdColumn,
  ];

  readonly deleteActionColumn: ColumnDefinition<DeviceDto> = {
    type: 'action',
    actionType: 'button',
    testId: 'remove-from-company',
    header: 'Actions',
    iconName: 'delete',
    buttonName: this.translateService.instant('Geo_assets.delete'),
    buttonCallback: (row) => this.promptRemoveDevice(row),
    isDisabled: () => this.deviceStore.busy() || !this.canDeleteDevices(),
    tooltip: () =>
      this.canDeleteDevices() ? '' : this.deletePermissionTooltip,
  } as ActionButtonColumnDefinition<DeviceDto>;
  readonly allResourcesActionMenuColumn: ColumnDefinition<DeviceDto> = {
    type: 'action',
    actionType: 'menu',
    header: 'Actions',
    actions: [
      {
        label: this.translateService.instant('resources.actions.edit_device'),
        callback: (row) => this.openEditDeviceModal(row),
        isDisabled: () => this.deviceStore.busy(),
      },
      {
        label: this.translateService.instant('resources.actions.unassign'),
        callback: (row) => this.promptUnassignDevice(row),
      },
      {
        label: this.translateService.instant(
          'resources.actions.delete_permanently',
        ),
        callback: (row) => this.promptDeleteDevicePermanently(row),
        isDisabled: () => !this.canDeleteDevices(),
        tooltip: () =>
          this.canDeleteDevices() ? '' : this.deletePermissionTooltip,
      },
    ] as TableAction<DeviceDto>[],
  } as ActionMenuColumnDefinition<DeviceDto>;

  readonly DEVICE_CSV_COLUMNS: CsvColumn<DeviceDto>[] = [
    {
      header: this.translateService.instant('resources.table.name'),
      accessor: 'nameAlias',
      format: (_, row) => this.getName(row),
    },
    {
      header: this.translateService.instant('resources.table.device_type'),
      accessor: 'details.type',
      format: (_, row) => this.getDeviceType(row),
    },
    {
      header: this.translateService.instant('resources.table.model'),
      accessor: 'details.aircraftModel.name',
      format: (_, row) => this.getAircraftModelName(row),
    },
    {
      header: this.translateService.instant('resources.table.unit_id'),
      accessor: 'details.unitId',
      format: (_, row) => this.getUnitId(row),
    },
    {
      header: this.translateService.instant('resources.table.unit_id'),
      accessor: 'details.description',
      format: (_, row) => this.getUnitId(row),
    },
  ];

  constructor() {
    effect(() => {
      const devices = this.deviceStore.devices();
      this.rebuildFilterOptions(devices);
      this.refreshFilteredDevices(devices);
    });
  }

  get devices(): DeviceDto[] {
    return this.deviceStore.devices();
  }

  ngOnInit(): void {
    this.columns = this.resolveColumns();

    if (this.useAllDevices) {
      if (this.deviceStore.loading()) {
        return;
      }
      this.deviceStore.getAllDevices('BRIEF');
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['useAllDevices'] || changes['showDeleteAction']) {
      this.columns = this.resolveColumns();
      this.rebuildFilterOptions(this.deviceStore.devices());
      this.refreshFilteredDevices(this.deviceStore.devices());
    }
  }

  onDeviceTypeFilterChange(value: string): void {
    this.selectedDeviceType = value;
    this.refreshFilteredDevices();
  }

  onCompanyFilterChange(value: string): void {
    this.selectedCompany = value;
    this.refreshFilteredDevices();
  }

  onModelFilterChange(value: string): void {
    this.selectedModel = value;
    this.refreshFilteredDevices();
  }

  onUnitNoSearchChange(value: string): void {
    this.searchUnitNo = value;
    this.refreshFilteredDevices();
  }

  // Names each row after its device, so an end-to-end test addresses one device's row and cells
  // by id instead of searching the table for its name.
  deviceRowTestId = (device: DeviceDto): string =>
    device.deviceId?.trim() || '';

  goToDrone = (device: DeviceDto): void => {
    const deviceId = device.deviceId?.trim();

    if (!deviceId) {
      return;
    }

    this.router.navigate(['/device', deviceId], {
      queryParams: {
        returnTo: this.useAllDevices ? '/admin/all-resources' : '/resources',
      },
    });
  };

  promptRemoveDevice(device: DeviceDto): void {
    if (!this.canDeleteDevices()) {
      return;
    }

    const deviceId = device.deviceId?.trim();
    if (!deviceId) {
      return;
    }

    const deviceLabel = this.getName(device).trim() || deviceId;
    const dialogRef = this.dialog.open(InformationModalComponent, {
      data: {
        headerText: 'Remove Device',
        bodyText: `Are you sure you want to remove "${deviceLabel}" from your company?\n\nThis action cannot be undone.`,
        cancelable: true,
      },
    });

    dialogRef.closed.pipe(take(1)).subscribe((result) => {
      const dialogResult = result as { cancel?: boolean } | undefined;
      if (dialogResult?.cancel === false) {
        this.deviceStore.removeDeviceFromCompany(deviceId);
      }
    });
  }

  promptUnassignDevice(device: DeviceDto): void {
    const deviceId = device.deviceId?.trim();
    if (!deviceId) {
      return;
    }

    const deviceLabel = this.getName(device).trim() || deviceId;
    const dialogRef = this.dialog.open(InformationModalComponent, {
      data: {
        headerText: this.translateService.instant(
          'resources.confirm.unassign.title',
        ),
        bodyText: this.translateService.instant(
          'resources.confirm.unassign.body',
          {
            device: deviceLabel,
          },
        ),
        cancelable: true,
      },
    });

    dialogRef.closed.pipe(take(1)).subscribe((result) => {
      const dialogResult = result as { cancel?: boolean } | undefined;
      if (dialogResult?.cancel === false) {
        this.deviceStore.removeDeviceFromCompanyWithMode(
          deviceId,
          'mark-unassigned',
        );
      }
    });
  }

  promptDeleteDevicePermanently(device: DeviceDto): void {
    if (!this.canDeleteDevices()) {
      return;
    }

    const deviceId = device.deviceId?.trim();
    if (!deviceId) {
      return;
    }

    const deviceLabel = this.getName(device).trim() || deviceId;
    const dialogRef = this.dialog.open(InformationModalComponent, {
      data: {
        headerText: this.translateService.instant(
          'resources.confirm.delete_permanent.title',
        ),
        bodyText: this.translateService.instant(
          'resources.confirm.delete_permanent.body',
          {
            device: deviceLabel,
          },
        ),
        cancelable: true,
      },
    });

    dialogRef.closed.pipe(take(1)).subscribe((result) => {
      const dialogResult = result as { cancel?: boolean } | undefined;
      if (dialogResult?.cancel === false) {
        this.deviceStore.deleteDevice(deviceId);
      }
    });
  }

  openEditDeviceModal(device: DeviceDto): void {
    const deviceId = device.deviceId?.trim();
    if (!deviceId) {
      return;
    }

    this.deviceStore
      .getDeviceFromStoreOrFetch({ deviceId, projection: 'FULL' })
      .pipe(take(1))
      .subscribe({
        next: (fullDevice) => {
          const dialogRef = this.dialog.open(EditDeviceModalComponent, {
            data: { device: fullDevice },
          });

          dialogRef.closed.pipe(take(1)).subscribe((result) => {
            if (result && this.useAllDevices) {
              this.deviceStore.getAllDevices('BRIEF');
            }
          });
        },
      });
  }

  csvExportDevices(): void {
    this.csvExportService.exportToCsv(
      this.filteredDevices ?? [],
      this.DEVICE_CSV_COLUMNS,
      {
        fileName: `devices_${buildTimestamp()}.csv`,
      },
    );
  }

  private getName(device?: DeviceDto): string {
    return (
      device?.nameAlias?.trim() ||
      device?.deviceSummary?.defaultName?.trim() ||
      this.getAircraftModelName(device)
    );
  }

  private getNameTooltip(device?: DeviceDto): string {
    const name = this.getName(device).trim();
    const deviceId = device?.deviceId?.trim();

    if (name && deviceId) {
      return `${name} (${deviceId})`;
    }

    return name || deviceId || '';
  }

  private getCompanyLabel(device?: DeviceDto): string {
    const companyName = device?.company?.name?.trim();
    if (companyName) {
      return companyName;
    }

    const companyId = device?.company?.id?.trim();
    if (companyId) {
      return companyId;
    }

    return this.translateService.instant('resources.filter.unassigned');
  }

  private getDeviceType(device?: DeviceDto): string {
    return device?.details?.type?.trim() || device?.deviceType?.trim() || '-';
  }

  private getAircraftModelName(device?: DeviceDto): string {
    const summaryModelName = this.getSummaryModel(device);
    if (summaryModelName) {
      return summaryModelName;
    }

    const details = device?.details;
    if (
      !details ||
      (details.type !== 'QsUav' && details.type !== 'QsTrinityPro') ||
      !('aircraftModel' in details)
    ) {
      return '-';
    }
    return details.aircraftModel?.name?.trim() || '-';
  }

  private getUnitId(device?: DeviceDto): string {
    const unitId = this.getUnitIdValue(device);
    return unitId ?? '-';
  }

  private getUnitIdValue(device?: DeviceDto): string | null {
    const summaryUnitId = device?.deviceSummary?.unitId?.trim();
    if (summaryUnitId) {
      return summaryUnitId;
    }

    const details = device?.details;
    if (
      !details ||
      (details.type !== 'QsUav' && details.type !== 'QsTrinityPro') ||
      !('unitId' in details)
    ) {
      return null;
    }
    return details.unitId !== undefined ? String(details.unitId) : null;
  }

  private getSummaryModel(device?: DeviceDto): string {
    return (
      device?.deviceSummary?.model?.trim() ||
      device?.deviceSummary?.modelName?.trim() ||
      ''
    );
  }

  private canDeleteDevices(): boolean {
    const profile = this.profile();
    return !!(profile?.isMasteradmin || profile?.role === UserRole.ADMIN);
  }

  private getDeviceTypeFilterValue(device?: DeviceDto): string {
    return (device?.details?.type ?? '').trim();
  }

  private getCompanyFilterValue(device?: DeviceDto): string {
    const companyValue = (
      device?.company?.id ??
      device?.company?.name ??
      ''
    ).trim();
    return companyValue || this.unassignedCompanyFilterValue;
  }

  private getModelFilterValue(device?: DeviceDto): string {
    return this.getAircraftModelName(device);
  }

  private resolveColumns(): ColumnDefinition<DeviceDto>[] {
    if (this.useAllDevices) {
      return [...this.allResourcesColumns, this.allResourcesActionMenuColumn];
    }

    const columns = this.baseColumns;

    if (!this.showDeleteAction) {
      return columns;
    }

    return [...columns, this.deleteActionColumn];
  }

  private rebuildFilterOptions(devices: DeviceDto[]): void {
    const deviceTypeOptions = new Set<string>();
    const modelOptionMap = new Map<string, string>();
    const companyOptionMap = new Map<string, string>();

    devices.forEach((device) => {
      const deviceTypeValue = this.getDeviceTypeFilterValue(device);
      if (deviceTypeValue) {
        deviceTypeOptions.add(deviceTypeValue);
      }

      const companyValue = this.getCompanyFilterValue(device);
      if (companyValue) {
        companyOptionMap.set(companyValue, this.getCompanyLabel(device));
      }

      const modelValue = this.getModelFilterValue(device);
      if (modelValue) {
        modelOptionMap.set(modelValue, modelValue);
      }
    });

    this.deviceTypeFilterOptions = Array.from(deviceTypeOptions)
      .sort((a, b) => a.localeCompare(b))
      .map((value) => ({ value, label: value }));

    this.modelFilterOptions = Array.from(modelOptionMap.entries())
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([value, label]) => ({ value, label }));

    this.companyFilterOptions = Array.from(companyOptionMap.entries())
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([value, label]) => ({ value, label }));
  }

  private refreshFilteredDevices(devices: DeviceDto[] = this.devices): void {
    this.filteredDevices = devices.filter((device) => {
      if (
        !matchesSearch(this.searchUnitNo, [
          this.getUnitId(device),
          this.getName(device),
          device.deviceId,
        ])
      ) {
        return false;
      }

      if (
        this.selectedDeviceType !== this.allFilterValue &&
        this.getDeviceTypeFilterValue(device) !== this.selectedDeviceType
      ) {
        return false;
      }

      if (
        this.selectedModel !== this.allFilterValue &&
        this.getModelFilterValue(device) !== this.selectedModel
      ) {
        return false;
      }

      if (
        this.useAllDevices &&
        this.selectedCompany !== this.allFilterValue &&
        this.getCompanyFilterValue(device) !== this.selectedCompany
      ) {
        return false;
      }

      return true;
    });
  }
}
